import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Col, Row } from 'react-bootstrap';
import { ICategoryArticle } from "../../helpers/interfaces/ranking";
import Sidebar from "../../components/Sidebar";
import CustomPagination from "../../common/Pagination";
import eventBus from "../../common/EventBus";
import { categoryService } from "../../services/CategoryService";
import { FacebookIcon, InstagramIcon, TwitterIcon, WhatsAppIcon } from "../../components/Icons";
import { Article } from "../Categorie/Categoria/Article";
import { matchesService } from "../../services/MatchesService";
import { useMenuSingleton } from '../../singleton/menuSingleton';
import Advertising from "../../common/Advertising";
import './Articolo.css';

export default function ArticoloPage() {
    const { getMenu, getSlugPath } = useMenuSingleton();
    const { id } = useParams<{ id: string }>();
    const [articleData, setArticleData] = useState<ICategoryArticle | null>(null);
    const [currentArticles, setCurrentArticles] = useState<ICategoryArticle[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [articles, setArticles] = useState<ICategoryArticle[]>([]);
    const pageLimit = 8;

    const fetchArticles = useCallback(async (categoryId: number) => {
        const articles = await categoryService.getCategoryArticles(categoryId);
        if (articles && typeof articles !== "string") {
            setArticles(articles.ultimiPerCategoria);
            setCurrentArticles(articles.ultimiPerCategoria.slice(0, pageLimit));
        }
    }, [pageLimit]);

    const fetchArticleData = useCallback(async (articleId: string) => {
        eventBus.dispatch("showLoader", { text: "Caricamento in corso..." });
        try {
            const data = await matchesService.getArticleData(articleId)
            if (data) {
                setArticleData(data);
                await fetchArticles(data.idCategoria);
            }
        } catch (error) {
            console.error("Error fetching article data: ", error);
        } finally {
            eventBus.dispatch("hideLoader", {});
        }
    }, [fetchArticles]);

    useEffect(() => {
        if (id) {
            fetchArticleData(id);
        }
    }, [fetchArticleData, id]);

    const onPageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
        const start = (pageNumber - 1) * pageLimit;
        const end = start + pageLimit;
        setCurrentArticles(articles.slice(start, end));
    };

    const handlePrint = () => {
        window.print();
    };

    const slugPath = getSlugPath(getMenu().data);
    const categoryPath = slugPath["_articoli_" + articleData?.idCategoria];

    return (
        <React.Fragment>
            <Row id="page_content">
                <Col id="content" xs={12} lg={9}>
                    <h1>{articleData?.titolo}</h1>
                    <div className="article-center">
                        <div className="article-header">
                            {articleData?.img1 && (
                                <div className="image-container">
                                    <img className="article-image" src={articleData.img1} alt={articleData?.titolo} />
                                </div>
                            )}

                            <div className="title-share">
                                <div className="title-container">
                                    <div className="article-info">
                                        <p>
                                            <span>{articleData?.dataPubblicazioneData} - </span>
                                            <span className="author-name">{articleData?.nomeAutore}</span>
                                        </p>
                                        <p>Categoria: <Link to={categoryPath}>{articleData?.nomeCategoria}</Link></p>
                                        <p>Società: <Link to={`/societa/${articleData?.idSocieta}`}>{articleData?.nomeSocieta}</Link></p>
                                        <p>Visualizzazioni: {articleData?.letture}</p>
                                    </div>
                                </div>

                                <div className="share-to-social-media">
                                    <div className="social-media-actions">
                                        <a className="social-media-svg" href={`https://twitter.com/share?url=${encodeURIComponent(document.location.href)}&text=${encodeURIComponent(articleData?.titolo ?? '')}`} target="_blank" title="Twitter" rel="noopener noreferrer">
                                            <TwitterIcon />
                                        </a>
                                        <a className="social-media-svg" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(document.location.href)}`} target="_blank" title="Facebook" rel="noopener noreferrer">
                                            <FacebookIcon />
                                        </a>
                                        <a className="social-media-svg" href={`https://www.instagram.com/?url=${encodeURIComponent(document.location.href)}`} target="_blank" title="Instagram" rel="noopener noreferrer">
                                            <InstagramIcon />
                                        </a>
                                        <a className="social-media-svg" href={`https://api.whatsapp.com/send?text=${encodeURIComponent(articleData?.titolo ?? ' ' + document.location.href)}`} target="_blank" title="WhatsApp" rel="noopener noreferrer">
                                            <WhatsAppIcon />
                                        </a>
                                        <div className="social-media-print" onClick={handlePrint}>Stampa l’articolo</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="article-content">
                            <div dangerouslySetInnerHTML={{ __html: articleData?.articolo || '' }} />
                        </div>
                    </div>

                    <div className="related-articles">
                        <hr />
                        <Advertising code="new_banner_articolo_1" as="div" className='text-center py-4' />
                        <h2 className="mt-2 mb-5">Potrebbe interessarti anche...</h2>
                        {
                            currentArticles.length > 0 ? currentArticles.map((article, index) => (
                                <React.Fragment key={article.id}>
                                    {index !== 0 && index % 4 === 0 && (
                                        <Advertising code="new_banner_articolo_2" as="div" className='text-center py-4' />
                                    )}
                                    <Article article={article} />
                                </React.Fragment>
                            )) : <Row className="pb-3 d-flex justify-content-center">Nessuna informazione trovata</Row>
                        }
                        <div className="d-flex justify-content-center">
                            <CustomPagination
                                totalRecords={articles.length}
                                pageLimit={pageLimit}
                                pageNeighbours={2}
                                onPageChanged={onPageChange}
                                currentPage={currentPage}
                            />
                        </div>
                    </div>
                </Col>
                <Col id="sidebar" xs={12} lg={3} className="pt-3">
                    <Sidebar />
                </Col>
            </Row>
        </React.Fragment >
    );
}
